import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate} from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { clearUser } from "../store/authSlice";
import { syncUserProfileFromApi } from "../services/syncUserProfile";
import {
    addWsListener,
    closeWs,
    connectWs,
    getWsDebugSnapshot,
    sendWs,
    isWsOpen,
    WS_ACTIVE_CHAT_ID_KEY
} from "../services/wsClient";
import { applyOwnerPropertyApprovalFromSocket } from "../utils/propertyAccess";

const WS_URL = process.env.REACT_APP_WEBSOCKET_URL;
const API_URL = process.env.REACT_APP_API_URL;

const Header = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((state) => state?.auth?.user);

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifications, setShowNotifications] = useState(false);
    const [notificationPermission, setNotificationPermission] = useState('default');
    const [showAllNotifications, setShowAllNotifications] = useState(false);
    const [avatarFailed, setAvatarFailed] = useState(false);

    const notificationRef = useRef(null);
    const seenNotificationIdsRef = useRef(new Set());
    /** chatId -> last notified message key (socket may not push private DMs). */
    const lastPrivateChatPreviewRef = useRef(new Map());
    const privateChatPollSeededRef = useRef(false);
    /** Private rooms this browser has joined — used to match inbound socket events. */
    const joinedChatIdsRef = useRef(new Set());
    /** Chat ids returned by user.getChats for this user (never notify for someone else's DM). */
    const myPrivateChatIdsRef = useRef(new Set());
    const MAX_VISIBLE = 5;

    const chatIncludesUser = useCallback((chat, selfId) => {
        if (!chat || !selfId) return false;
        const participants = Array.isArray(chat.participants) ? chat.participants : [];
        if (!participants.length) {
            // getChats often omits participants — treat as mine only if it came from my getChats list.
            const cid = String(chat.chatId || chat._id || "").trim();
            return cid ? myPrivateChatIdsRef.current.has(cid) : false;
        }
        return participants.some((person) => {
            const nested = person?.userId;
            const pid =
                (nested && typeof nested === "object" ? nested._id || nested.id : nested) ||
                person?._id ||
                person?.id ||
                person?.userid;
            return pid != null && String(pid) === String(selfId);
        });
    }, []);

    const sameUserId = useCallback((left, right) => {
        if (left == null || right == null || left === "" || right === "") return false;
        return String(left).trim() === String(right).trim();
    }, []);

    const pickUserId = useCallback((value) => {
        if (value == null || value === "") return "";
        if (typeof value === "object") {
            return String(value._id || value.id || value.userId || value.userid || "").trim();
        }
        return String(value).trim();
    }, []);

    const displayName = user?.name || "User";
    const profileImage = String(user?.profileImage || "").trim();
    const initial =
        (displayName && displayName.trim().charAt(0).toUpperCase()) ||
        (user?.email && user.email.trim().charAt(0).toUpperCase()) ||
        "U";

    useEffect(() => {
        setAvatarFailed(false);
    }, [profileImage]);

    // Hydrate avatar from getProfile (login often omits profileImage)
    useEffect(() => {
        const userId =
            user?.userid ||
            localStorage.getItem("userid") ||
            localStorage.getItem("userId") ||
            "";
        if (!userId) return undefined;

        let cancelled = false;
        (async () => {
            if (cancelled) return;
            await syncUserProfileFromApi(dispatch, userId);
        })();

        return () => {
            cancelled = true;
        };
    }, [dispatch, user?.userid]);

    // Track permission only — do not call requestPermission on mount (Chrome requires a user gesture for a reliable prompt).
    useEffect(() => {
        if (!('Notification' in window)) {
            setNotificationPermission('unsupported');
            return;
        }
        setNotificationPermission(Notification.permission);

        let statusObj = null;
        const sync = () => setNotificationPermission(Notification.permission);

        try {
            const q = navigator.permissions?.query?.({ name: 'notifications' });
            if (q && typeof q.then === 'function') {
                q.then((status) => {
                    statusObj = status;
                    status.addEventListener('change', sync);
                    sync();
                }).catch(() => {});
            }
        } catch (_) {
            /* ignore */
        }

        return () => {
            if (statusObj) {
                try {
                    statusObj.removeEventListener('change', sync);
                } catch (_) {
                    /* ignore */
                }
            }
        };
    }, []);

    // Mark notification as read (socket-only / local UI)
    const getStoredUserId = useCallback(
        () =>
            localStorage.getItem("userid") ||
            localStorage.getItem("userId") ||
            localStorage.getItem("id") ||
            "",
        []
    );

    const markAsRead = useCallback(async (notificationId) => {
        try {
            const userId = getStoredUserId();
            if (!userId) return;

            setNotifications(prev =>
                prev.map(n => (n?._id === notificationId ? { ...n, isRead: true } : n))
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
        }
    }, [getStoredUserId]);

    // Show desktop notification
    const showDesktopNotification = useCallback((notification) => {
        if (!('Notification' in window)) {
            console.log('This browser does not support desktop notifications');
            return;
        }

        if (Notification.permission === 'granted') {
            const d = notification.data || {};
            const notificationOptions = {
                body: notification.message,
                icon: '/Assets/Images/color icon_circle.png',
                badge: '/Assets/Images/color icon_circle.png',
                tag: notification._id || 'notification-' + Date.now(),
                requireInteraction: false,
                data: {
                    notificationId: notification._id,
                    taskId: d.taskId,
                    type: notification.type,
                    chatId: d.chatId,
                    senderUserId: d.senderUserId,
                    messageId: d.messageId
                },
                vibrate: [200, 100, 200]
            };

            // Stable tag prevents Chrome from stacking duplicate banners for the same message.
            const desktopNotification = new Notification(notification.title, {
                ...notificationOptions,
                tag: String(notification._id || notificationOptions.tag)
            });

            desktopNotification.onclick = (event) => {
                event.preventDefault();
                window.focus();
                const payload = notification.data || {};
                if (notification.type === 'group-message' && payload.group) {
                    navigate('/communication', {
                        state: { focusGroup: payload.group }
                    });
                } else if (notification.type === 'chat-message' && (payload.chatId || payload.senderUserId)) {
                    if (payload.chatId) {
                        localStorage.setItem(WS_ACTIVE_CHAT_ID_KEY, String(payload.chatId));
                    }
                    navigate('/communication', {
                        state: {
                            focusChatId: payload.chatId || undefined,
                            focusParticipantId: payload.senderUserId || undefined
                        }
                    });
                } else if (payload.taskId) {
                    navigate(`/taskView/${payload.taskId}`);
                }
                markAsRead(notification._id);
                desktopNotification.close();
            };

            desktopNotification.onclose = () => {
                console.log('Desktop notification closed');
            };

            desktopNotification.onerror = (error) => {
                console.error('Desktop notification error:', error);
            };

            setTimeout(() => {
                desktopNotification.close();
            }, 10000);
        }
        /* Permission "default" / "denied": user must use the Enable control (gesture) or site settings — do not request here. */
    }, [markAsRead, navigate]);

    // Handle click outside notification dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Mark all notifications as read (socket-only / local UI)
    const markAllAsRead = async () => {
        const userId = getStoredUserId();
        if (!userId) return;

        try {
            // Update UI immediately
            setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    // Show banner notification
    const showBannerNotification = useCallback((notification) => {
        showDesktopNotification(notification);
    }, [showDesktopNotification]);

    // Test function to simulate new notifications (for development)
    const testNewNotification = useCallback(() => {
        const testNotification = {
            _id: 'test-' + Date.now(),
            title: '🔔 Test Notification',
            message: 'This is a test notification to verify the system is working.',
            type: 'test',
            isRead: false,
            createdAt: new Date().toISOString(),
            data: {
                testData: 'This is test data'
            }
        };

        // Add to notifications list
        setNotifications(prev => [testNotification, ...prev]);
        setUnreadCount(prev => prev + 1);
        
        // Show desktop notification
        showBannerNotification(testNotification);
        
        console.log('Test notification added:', testNotification);
    }, [showBannerNotification]);

    // Test function to simulate property approval (for owner debugging)
    const testPropertyApproval = useCallback(() => {
        const currentRole = localStorage.getItem("role");
        const currentUserId = localStorage.getItem("userid");
        
        if (currentRole === "OWNER") {
            const testApprovalMessage = {
                action: "propertyInfo.updated",
                ownerId: currentUserId,
                targetUserId: currentUserId,
                userId: currentUserId,
                propertyInfo: "Approved",
                adminId: "test-admin-id"
            };
            
            console.log("[Header] 🧪 Simulating property approval message:", testApprovalMessage);
            const handled = applyOwnerPropertyApprovalFromSocket(testApprovalMessage);
            console.log("[Header] 🧪 Simulation result:", handled);
            
            if (handled) {
                toast.success("✅ SIMULATION: Property approval works! This proves the frontend can handle the WebSocket message.");
            } else {
                toast.warning("❌ SIMULATION: Property approval handling failed. Check console for details.");
            }
        } else {
            toast.info(`Current role is ${currentRole}. Property approval simulation is only for OWNER role.`);
        }
    }, []);

    // Expose test functions to window for debugging
    useEffect(() => {
        window.testNotification = testNewNotification;
        window.testPropertyApproval = testPropertyApproval;
        console.log('Test function available: window.testNotification()');
        console.log('Property approval test function available: window.testPropertyApproval()');
    }, [testNewNotification, testPropertyApproval]);

    // Start empty; notifications come from WebSocket events

    const sendJoinChat = useCallback((chatId, userId, role) => {
        if (!chatId || !userId || !isWsOpen()) return false;
        const id = String(chatId);
        joinedChatIdsRef.current.add(id);
        return sendWs({
            action: "join_chat",
            chatId: id,
            userId,
            userid: userId,
            id: userId,
            userType: role,
            role
        });
    }, []);

    // Join last-selected chat from Header only for non-admin roles (e.g. OWNER / TENANT).
    // ADMIN / SUPERADMIN talk to many tenants — Communication page joins when they pick a chat.
    const joinActiveChatIfAny = useCallback(() => {
        const userId = getStoredUserId();
        const chatId = localStorage.getItem(WS_ACTIVE_CHAT_ID_KEY);
        if (!userId || !chatId) return;
        const role = (localStorage.getItem("role") || "user").toUpperCase();
        if (role === "ADMIN" || role === "SUPERADMIN") {
            return;
        }
        const ok = sendJoinChat(chatId, userId, role);
        console.log("[Header] join_chat active", { chatId, ok });
    }, [getStoredUserId, sendJoinChat]);

    // Owner/tenant must stay joined to every private chat, not only the open thread.
    // Otherwise admin DMs never arrive while the user is on Dashboard or another chat.
    const joinAllUserChatRooms = useCallback(async () => {
        const userId = getStoredUserId();
        const role = (localStorage.getItem("role") || "user").toUpperCase();
        if (!userId || !API_URL) return;
        if (role === "ADMIN" || role === "SUPERADMIN") return;
        if (!isWsOpen()) return;
        try {
            const response = await axios.post(API_URL, {
                action: "user.getChats",
                userid: userId,
                page: 1,
                limit: 100
            });
            const data = response?.data;
            const chats = Array.isArray(data?.chats) ? data.chats : [];
            const ids = [];
            chats.forEach((chat) => {
                const chatId = chat?.chatId || chat?._id;
                if (!chatId) return;
                const id = String(chatId);
                if (ids.includes(id)) return;
                ids.push(id);
                sendJoinChat(id, userId, role);
            });
            const activeId = localStorage.getItem(WS_ACTIVE_CHAT_ID_KEY);
            if (!activeId && ids[0]) {
                localStorage.setItem(WS_ACTIVE_CHAT_ID_KEY, ids[0]);
            }
            console.log("[Header] joined all private chats", { count: ids.length, ids });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[Header] joinAllUserChatRooms failed:", e?.message || e);
            joinActiveChatIfAny();
        }
    }, [getStoredUserId, sendJoinChat, joinActiveChatIfAny]);

    const privateMessagePreviewKey = useCallback((chatId, lastMessage) => {
        if (!chatId || !lastMessage) return "";
        const content = String(lastMessage.content || lastMessage.fileName || "").trim();
        const msgId =
            lastMessage._id ||
            lastMessage.id ||
            lastMessage.messageId ||
            "";
        // Never key off empty content + changing timestamps (creates dummy repeats).
        if (!content && !msgId) return "";
        if (msgId) return `${chatId}:${msgId}`;
        return `${chatId}:${content}`;
    }, []);

    const markNotificationSeen = useCallback((...keys) => {
        let already = false;
        keys.forEach((key) => {
            const k = String(key || "").trim();
            if (!k) return;
            if (seenNotificationIdsRef.current.has(k)) already = true;
            seenNotificationIdsRef.current.add(k);
        });
        return already;
    }, []);

    const emitPrivateChatNotification = useCallback(
        ({
            chatId,
            message,
            senderUserId,
            senderName,
            chat = null,
            joinRoom = true,
            source = "socket"
        }) => {
            const userId = getStoredUserId();
            if (!userId || !chatId || !message) return false;
            if (sameUserId(senderUserId, userId)) return false;

            const content = String(message.content || message.fileName || "").trim();
            const msgId = String(
                message._id || message.id || message.messageId || ""
            ).trim();
            // Do not notify or sync empty/dummy bubbles — only real messages.
            if (!content && !msgId) return false;

            const chatKey = String(chatId);
            const previewKey = privateMessagePreviewKey(chatId, message);
            if (!previewKey) return false;

            // Socket often has message _id; poll lastMessage may only have content.
            // Dedup both ways so each message only banners once.
            const idKey = msgId ? `chat-id-${msgId}` : "";
            const contentKey = content ? `chat-content-${chatKey}:${content}` : "";
            const notifId = idKey || contentKey || `chat-${previewKey}`;
            const alreadySeen =
                (idKey && seenNotificationIdsRef.current.has(idKey)) ||
                (contentKey && seenNotificationIdsRef.current.has(contentKey)) ||
                seenNotificationIdsRef.current.has(notifId) ||
                lastPrivateChatPreviewRef.current.get(chatKey) === previewKey ||
                (content &&
                    lastPrivateChatPreviewRef.current.get(`${chatKey}:content`) === content);

            if (alreadySeen) {
                lastPrivateChatPreviewRef.current.set(chatKey, previewKey);
                if (content) {
                    lastPrivateChatPreviewRef.current.set(`${chatKey}:content`, content);
                }
                markNotificationSeen(notifId, idKey, contentKey);
                return false;
            }

            lastPrivateChatPreviewRef.current.set(chatKey, previewKey);
            if (content) {
                lastPrivateChatPreviewRef.current.set(`${chatKey}:content`, content);
            }
            markNotificationSeen(notifId, idKey, contentKey);

            const notification = {
                _id: notifId,
                title: senderName || "New message",
                message: content || "Sent you a message",
                type: "chat-message",
                isRead: false,
                createdAt:
                    message.createdAt ||
                    message.timestamp ||
                    new Date().toISOString(),
                data: {
                    chatId: chatKey,
                    messageId: String(msgId || previewKey),
                    ...(senderUserId ? { senderUserId: String(senderUserId) } : {})
                }
            };

            console.log("[Header] private chat banner", { source, notification });
            showBannerNotification(notification);
            setNotifications((prev) => {
                if (prev.some((n) => n?._id === notifId)) return prev;
                return [notification, ...prev].slice(0, 50);
            });
            setUnreadCount((prev) => prev + 1);

            // Update sidebar + open thread when this chat is open (poll or socket).
            window.dispatchEvent(
                new CustomEvent("adukkumadi:chatSidebarSync", {
                    detail: {
                        chatId: chatKey,
                        message: {
                            ...message,
                            _id: msgId || message._id || `preview-${previewKey}`,
                            content,
                            chatId: chatKey
                        },
                        chat,
                        source,
                        appendToThread: Boolean(content)
                    }
                })
            );

            const role = (localStorage.getItem("role") || "user").toUpperCase();
            if (joinRoom && role !== "ADMIN" && role !== "SUPERADMIN") {
                sendJoinChat(chatId, userId, role);
            }
            return true;
        },
        [
            getStoredUserId,
            sameUserId,
            privateMessagePreviewKey,
            markNotificationSeen,
            showBannerNotification,
            sendJoinChat
        ]
    );

    /**
     * Always-on Header listener helper: accept a private message when
     * recipient id matches me, or chatId is one of my joined rooms.
     */
    const handlePrivateSocketEvent = useCallback(
        (data) => {
            const localUserId = getStoredUserId();
            if (!localUserId || !data || typeof data !== "object") return false;

            const groupName = String(data.group || data.groupType || data.message?.group || "")
                .trim()
                .toUpperCase();
            if (groupName === "OWNERS" || groupName === "TENANTS" || groupName === "EXECUTIVES") {
                return false;
            }

            const action = String(data.action || data.type || data.event || "").toLowerCase();
            if (
                action.includes("typing") ||
                action.includes("messages_read") ||
                action === "user_online" ||
                action === "user_offline" ||
                action === "ping" ||
                action === "pong" ||
                action === "join_chat" ||
                action === "leave_chat"
            ) {
                return false;
            }

            const msg =
                data.message ||
                data.msg ||
                data.data?.message ||
                data.payload?.message ||
                data.result?.message ||
                null;
            const chatId = String(
                data.chatId ||
                    data.data?.chatId ||
                    data.payload?.chatId ||
                    msg?.chatId ||
                    data.chat ||
                    ""
            ).trim();
            const content = String(
                (msg && (msg.content || msg.fileName)) || data.content || data.text || ""
            ).trim();
            const isNamedChatEvent =
                action === "new_message" ||
                action === "send_message" ||
                action === "chat.sendmessage" ||
                action === "chat.newmessage" ||
                action === "message_created" ||
                action === "chat_message" ||
                action === "message" ||
                action === "broadcast";

            // Require real text/file — empty frames were creating dummy "Just now" bubbles.
            if (!content) return false;
            if (!isNamedChatEvent && !(msg && typeof msg === "object")) return false;
            if (!chatId && !msg) return false;

            const message =
                msg && typeof msg === "object"
                    ? { ...msg, content: String(msg.content || msg.fileName || content).trim() }
                    : {
                          _id: data.messageId || data._id || `ws-${Date.now()}`,
                          content,
                          messageType: data.messageType || "text",
                          senderId: data.senderId || data.userId || data.userid,
                          createdAt: data.createdAt || new Date().toISOString(),
                          chatId
                      };

            const senderUserId =
                pickUserId(message.senderId) ||
                pickUserId(data.senderId) ||
                pickUserId(data.userId) ||
                pickUserId(data.userid) ||
                "";

            const recipientCandidates = [
                data.recipientId,
                data.recipientUserId,
                data.targetUserId,
                data.toUserId,
                data.to,
                data.receiverId,
                message.recipientId,
                message.recipientUserId,
                message.targetUserId,
                message.toUserId
            ]
                .map(pickUserId)
                .filter(Boolean);

            const recipientMatchesMe = recipientCandidates.some((id) =>
                sameUserId(id, localUserId)
            );
            const chatMatchesMe =
                Boolean(chatId) &&
                (joinedChatIdsRef.current.has(String(chatId)) ||
                    myPrivateChatIdsRef.current.has(String(chatId)));
            // Do NOT accept every frame on this socket. Admin connections often receive
            // other people's private DMs (tenant↔executive); only notify for my chats.
            const accepted = recipientMatchesMe || chatMatchesMe;
            console.log("[Header] private id match", {
                accepted,
                localUserId,
                senderUserId,
                recipientCandidates,
                recipientMatchesMe,
                chatId,
                chatMatchesMe,
                action: data.action || data.type
            });
            if (!accepted) return false;
            if (sameUserId(senderUserId, localUserId)) return false;

            const senderRaw = message.senderId;
            const senderName =
                (senderRaw &&
                    typeof senderRaw === "object" &&
                    (senderRaw.name || senderRaw.fullName)) ||
                message.senderName ||
                data.senderName ||
                "New message";

            return emitPrivateChatNotification({
                chatId: chatId || message.chatId || `unknown-${Date.now()}`,
                message,
                senderUserId,
                senderName,
                source: "socket"
            });
        },
        [getStoredUserId, pickUserId, sameUserId, emitPrivateChatNotification]
    );

    const notifyPrivateChatFromPreview = useCallback(
        (chat, { joinRoom = true } = {}) => {
            const userId = getStoredUserId();
            const chatId = chat?.chatId || chat?._id;
            const lastMessage = chat?.lastMessage;
            if (!userId || !chatId || !lastMessage) return;

            const senderUserId = pickUserId(lastMessage.senderId);
            if (sameUserId(senderUserId, userId)) return;

            const other = chat.otherParticipant || chat.participant || {};
            const senderRaw = lastMessage.senderId;
            const senderName =
                (senderRaw && typeof senderRaw === "object" && (senderRaw.name || senderRaw.fullName)) ||
                other.name ||
                other.fullName ||
                "New message";

            emitPrivateChatNotification({
                chatId,
                message: {
                    _id: lastMessage._id || lastMessage.id || lastMessage.messageId,
                    content: lastMessage.content,
                    fileName: lastMessage.fileName,
                    messageType: lastMessage.messageType || "text",
                    senderId: lastMessage.senderId || senderUserId,
                    createdAt:
                        lastMessage.timestamp ||
                        lastMessage.createdAt ||
                        new Date().toISOString(),
                    chatId: String(chatId)
                },
                senderUserId,
                senderName,
                chat,
                joinRoom,
                source: "poll"
            });
        },
        [getStoredUserId, pickUserId, sameUserId, emitPrivateChatNotification]
    );

    // Private DM socket push is unreliable; poll getChats on every page (Header is global).
    const pollPrivateChatNotifications = useCallback(async () => {
        const userId = getStoredUserId();
        if (!userId || !API_URL) return;
        try {
            const response = await axios.post(API_URL, {
                action: "user.getChats",
                userid: userId,
                page: 1,
                limit: 100
            });
            const data = response?.data;
            const chats = Array.isArray(data?.chats) ? data.chats : [];
            const seedOnly = !privateChatPollSeededRef.current;
            const mine = new Set();

            chats.forEach((chat) => {
                const chatId = chat?.chatId || chat?._id;
                if (!chatId) return;
                if (!chatIncludesUser(chat, userId) && Array.isArray(chat.participants) && chat.participants.length) {
                    return;
                }
                const id = String(chatId);
                mine.add(id);
                myPrivateChatIdsRef.current.add(id);
                joinedChatIdsRef.current.add(id);

                const lastMessage = chat?.lastMessage;
                if (!lastMessage) return;
                const content = String(
                    lastMessage.content || lastMessage.fileName || ""
                ).trim();
                if (!content) return;
                const previewKey = privateMessagePreviewKey(chatId, lastMessage);
                if (!previewKey) return;

                if (seedOnly) {
                    lastPrivateChatPreviewRef.current.set(id, previewKey);
                    lastPrivateChatPreviewRef.current.set(`${id}:content`, content);
                    return;
                }

                const previous = lastPrivateChatPreviewRef.current.get(id);
                const previousContent = lastPrivateChatPreviewRef.current.get(`${id}:content`);
                if (previous === previewKey || previousContent === content) {
                    return;
                }
                notifyPrivateChatFromPreview(chat);
            });

            // Drop ids that are no longer in my getChats list.
            myPrivateChatIdsRef.current = mine;

            if (seedOnly) {
                privateChatPollSeededRef.current = true;
                // On login, surface unread private chats so the user knows to open Communication.
                chats.forEach((chat) => {
                    const chatId = chat?.chatId || chat?._id;
                    if (!chatId || !mine.has(String(chatId))) return;
                    const unread = Number(chat?.unreadForMe ?? chat?.unreadCount ?? 0);
                    if (!Number.isFinite(unread) || unread <= 0) return;
                    const content = String(
                        chat?.lastMessage?.content || chat?.lastMessage?.fileName || ""
                    ).trim();
                    if (!content) return;
                    lastPrivateChatPreviewRef.current.delete(String(chatId));
                    lastPrivateChatPreviewRef.current.delete(`${chatId}:content`);
                    notifyPrivateChatFromPreview(chat);
                });
                console.log("[Header] private chat poll seeded", {
                    count: lastPrivateChatPreviewRef.current.size,
                    myChats: mine.size
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[Header] private chat poll failed:", e?.message || e);
        }
    }, [getStoredUserId, privateMessagePreviewKey, notifyPrivateChatFromPreview, chatIncludesUser]);

    useEffect(() => {
        const userId = getStoredUserId();
        if (!userId || !API_URL) return undefined;

        pollPrivateChatNotifications();
        const interval = setInterval(pollPrivateChatNotifications, 8000);
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                pollPrivateChatNotifications();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [getStoredUserId, pollPrivateChatNotifications]);

    const handlePrivateSocketEventRef = useRef(handlePrivateSocketEvent);
    handlePrivateSocketEventRef.current = handlePrivateSocketEvent;
    const showBannerNotificationRef = useRef(showBannerNotification);
    showBannerNotificationRef.current = showBannerNotification;
    const joinActiveChatIfAnyRef = useRef(joinActiveChatIfAny);
    joinActiveChatIfAnyRef.current = joinActiveChatIfAny;
    const joinAllUserChatRoomsRef = useRef(joinAllUserChatRooms);
    joinAllUserChatRoomsRef.current = joinAllUserChatRooms;

    // Global WebSocket: connect once for the whole app (all routes).
    // Stable effect deps so we do not register two listeners and double-banner.
    useEffect(() => {
        const userId = getStoredUserId();
        const userType = localStorage.getItem('role') || 'user';
        console.log("[Header] socket setup", {
            userId,
            userType,
            WS_URL,
            propertyInfo: localStorage.getItem("propertyInfo")
        });
        if (!userId || !WS_URL) return undefined;

        connectWs({ baseUrl: WS_URL, userId, userType })
            .then(async () => {
                console.log("[Header] socket connected", getWsDebugSnapshot());
                joinActiveChatIfAnyRef.current?.();
                await joinAllUserChatRoomsRef.current?.();
            })
            .catch((error) => {
                console.log("[Header] socket connect failed", error?.message || error);
            });

        const onActiveChatChanged = () => {
            joinActiveChatIfAnyRef.current?.();
        };
        window.addEventListener("adukkumadi:activeChatChanged", onActiveChatChanged);

        const unsubscribe = addWsListener((data) => {
            console.log("[Header] Received WebSocket event:", data);
            console.log("[Header] listener snapshot", {
                role: localStorage.getItem("role"),
                userid: localStorage.getItem("userid"),
                propertyInfo: localStorage.getItem("propertyInfo"),
                socket: getWsDebugSnapshot()
            });
            
            const handled = applyOwnerPropertyApprovalFromSocket(data);
            console.log("[Header] Approval event handled:", handled, data);

            if (handled) {
                const ownerId = localStorage.getItem("userid") || "owner";
                const notifId = `property-approved-${ownerId}`;
                if (!seenNotificationIdsRef.current.has(notifId)) {
                    seenNotificationIdsRef.current.add(notifId);
                    const notification = {
                        _id: notifId,
                        title: "Property approved",
                        message: "Your property is approved. You can access the site now.",
                        type: "property-approved",
                        isRead: false,
                        createdAt: new Date().toISOString()
                    };
                    showBannerNotificationRef.current?.(notification);
                    setNotifications((prev) => [notification, ...prev].slice(0, 50));
                    setUnreadCount((prev) => prev + 1);
                }
            }

            // 1) Explicit notification payload from backend
            if (data?.type === 'notification' && data?.notification) {
                const n = data.notification;
                const id = n?._id || n?.id || `notif-${Date.now()}`;
                if (!seenNotificationIdsRef.current.has(id)) {
                    seenNotificationIdsRef.current.add(id);
                    const normalized = { ...n, _id: id, isRead: false, createdAt: n.createdAt || new Date().toISOString() };
                    showBannerNotificationRef.current?.(normalized);
                    setNotifications(prev => [normalized, ...prev].slice(0, 50));
                    setUnreadCount(prev => prev + 1);
                }
                return;
            }

            const groupName = String(
                data?.group || data?.groupType || data?.message?.group || ""
            )
                .trim()
                .toUpperCase();
            const groupTitles = {
                OWNERS: "Owners",
                TENANTS: "Tenants",
                EXECUTIVES: "Executive members"
            };
            const isGroupDeleteEvent =
                data?.action === "delete_group_message" ||
                data?.type === "group_message_deleted" ||
                data?.type === "group_message_updated" ||
                data?.action === "delete_message" ||
                data?.type === "message_deleted";
            const isGroupMessageEvent =
                !isGroupDeleteEvent &&
                (data?.type === "group_message" ||
                    data?.action === "send_group_message" ||
                    Boolean(groupTitles[groupName]));

            if (isGroupMessageEvent && groupTitles[groupName]) {
                const msg =
                    data?.message ||
                    data?.data?.message ||
                    data?.payload?.message ||
                    data;
                const senderRaw = msg?.senderId;
                const senderUserId =
                    senderRaw && typeof senderRaw === "object"
                        ? senderRaw?._id || senderRaw?.id
                        : senderRaw || data?.senderId || null;
                const localUserId = getStoredUserId();
                if (senderUserId && localUserId && String(senderUserId) === String(localUserId)) {
                    return;
                }

                const content = String(msg?.content || data?.content || "").trim();
                const msgId = String(msg?._id || msg?.id || "").trim();
                const idKey = msgId ? `group-id-${msgId}` : "";
                const contentKey = content
                    ? `group-content-${groupName}:${content}`
                    : "";
                const notifId =
                    idKey || contentKey || `group-${groupName}-${Date.now()}`;
                if (
                    (idKey && seenNotificationIdsRef.current.has(idKey)) ||
                    (contentKey && seenNotificationIdsRef.current.has(contentKey)) ||
                    seenNotificationIdsRef.current.has(notifId)
                ) {
                    return;
                }
                if (idKey) seenNotificationIdsRef.current.add(idKey);
                if (contentKey) seenNotificationIdsRef.current.add(contentKey);
                seenNotificationIdsRef.current.add(notifId);

                const senderName =
                    (senderRaw && typeof senderRaw === "object" && (senderRaw.name || senderRaw.fullName)) ||
                    msg?.senderName ||
                    "";
                const groupTitle = groupTitles[groupName];
                const notification = {
                    _id: notifId,
                    title: senderName ? `${senderName} · ${groupTitle}` : groupTitle,
                    message: content || `New message in ${groupTitle}`,
                    type: "group-message",
                    isRead: false,
                    createdAt: msg?.createdAt || new Date().toISOString(),
                    data: {
                        group: groupName,
                        messageId: msgId || notifId,
                        ...(senderUserId != null && senderUserId !== ""
                            ? { senderUserId: String(senderUserId) }
                            : {})
                    }
                };

                console.log("[Header] group banner", notification);
                showBannerNotificationRef.current?.(notification);
                setNotifications((prev) => {
                    if (prev.some((n) => n?._id === notifId)) return prev;
                    return [notification, ...prev].slice(0, 50);
                });
                setUnreadCount((prev) => prev + 1);
                return;
            }

            // 2) Private DM — always listen; emit banner when recipient/chat id matches me
            handlePrivateSocketEventRef.current?.(data);
        });

        return () => {
            window.removeEventListener("adukkumadi:activeChatChanged", onActiveChatChanged);
            unsubscribe();
        };
    }, [getStoredUserId]);

    // Format notification time
    const formatNotificationTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    // Handle notification click
    const handleNotificationClick = (notification) => {
        setNotifications((prev) =>
            prev.map((notif) =>
                notif._id === notification._id ? { ...notif, isRead: true } : notif,
            ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
        markAsRead(notification._id);

        const d = notification.data || {};

        if (notification.type === "group-message" && d.group) {
            navigate("/communication", {
                state: { focusGroup: d.group }
            });
            setShowNotifications(false);
            return;
        }

        if (notification.type === "chat-message" && (d.chatId || d.senderUserId)) {
            if (d.chatId) {
                localStorage.setItem(WS_ACTIVE_CHAT_ID_KEY, String(d.chatId));
            }
            navigate("/communication", {
                state: {
                    focusChatId: d.chatId || undefined,
                    focusParticipantId: d.senderUserId || undefined
                }
            });
            setShowNotifications(false);
            return;
        }

        if (d.taskId) {
            navigate(`/taskView/${d.taskId}`);
            setShowNotifications(false);
            return;
        }

        if (d.pollId) {
            navigate(`/e-voting-view/${d.pollId}`);
            setShowNotifications(false);
        }
    };

    const handleLogout = () => {
        closeWs();
        localStorage.clear();
        dispatch(clearUser());
        navigate('/');
    };

    return (
        <div>
            <nav className="main-header navbar navbar-expand navbar-white navbar-light">
                {/* Left navbar links */}
                <ul className="navbar-nav">
                    <li className="nav-item">
                        <button className="nav-link" data-widget="pushmenu" type="button" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                            <i className="fas fa-bars" />
                        </button>
                    </li>
                </ul>

                <ul className="navbar-nav ml-auto">
                    {/* Notification Bell */}
                    <li className="nav-item dropdown" ref={notificationRef}>
                        <button
                            type="button"
                            className="nav-link btn btn-link"
                            data-toggle="dropdown"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowNotifications(!showNotifications);
                            }}
                            style={{ border: "none", background: "transparent", boxShadow: "none" }}
                        >
                            <i className="far fa-bell" style={{ fontSize: '20px' }} />
                            {unreadCount > 0 && (
                                <span className="badge badge-danger navbar-badge" style={{ fontSize: '10px' }}>
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                            {(notificationPermission === 'denied' || notificationPermission === 'default') && unreadCount === 0 && (
                                <span className="badge badge-warning navbar-badge" style={{ fontSize: '9px' }} title="Notifications blocked">
                                    <i className="fas fa-exclamation" />
                                </span>
                            )}
                        </button>
                        <div 
                            className={`dropdown-menu dropdown-menu-lg dropdown-menu-right ${showNotifications ? 'show' : ''}`} 
                            style={{ width: '350px', maxHeight: '400px', overflowY: 'auto' }}
                        >
                            <div className="dropdown-item dropdown-header d-flex justify-content-between align-items-center">
                                <span>{unreadCount} Unread Notification{unreadCount !== 1 ? 's' : ''}</span>
                                {unreadCount > 0 && (
                                    <button 
                                        className="btn btn-sm btn-link p-0" 
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            markAllAsRead();
                                        }}
                                        style={{ fontSize: '12px' }}
                                    >
                                        Mark all read
                                    </button>
                                )}
                            </div>

                            {/* Desktop notification permission — prompt only from explicit click (user gesture). */}
                            {notificationPermission === 'denied' && (
                                <div className="dropdown-item" style={{ backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107' }}>
                                    <small className="d-flex align-items-center justify-content-between flex-wrap gap-1">
                                        <span style={{ color: '#856404' }}>
                                            <i className="fas fa-exclamation-triangle mr-1" />
                                            Notifications are blocked
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-warning"
                                            style={{ fontSize: '11px', padding: '2px 8px', whiteSpace: 'nowrap' }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // Open browser site settings for notification permissions
                                                const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
                                                const isFirefox = /Firefox/.test(navigator.userAgent);
                                                const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
                                                const isEdge = /Edg/.test(navigator.userAgent);

                                                let msg = '';
                                                if (isChrome || isEdge) {
                                                    msg = `To enable notifications:\n\n1. Click the 🔒 lock icon (or ⓘ) in the address bar\n2. Click "Site settings"\n3. Find "Notifications" → Set to "Allow"\n4. Refresh this page`;
                                                } else if (isFirefox) {
                                                    msg = `To enable notifications:\n\n1. Click the 🔒 lock icon in the address bar\n2. Click "Connection secure" → "More information"\n3. Go to "Permissions" tab\n4. Find "Send Notifications" → Uncheck "Block"\n5. Refresh this page`;
                                                } else if (isSafari) {
                                                    msg = `To enable notifications:\n\n1. Go to Safari menu → Preferences\n2. Click "Websites" tab\n3. Select "Notifications" from left sidebar\n4. Find this website → Set to "Allow"\n5. Refresh this page`;
                                                } else {
                                                    msg = `To enable notifications:\n\n1. Click the lock/info icon in the address bar\n2. Find "Notifications" in site settings\n3. Set to "Allow"\n4. Refresh this page`;
                                                }
                                                alert(msg);
                                            }}
                                        >
                                            <i className="fas fa-cog mr-1" />
                                            How to Enable
                                        </button>
                                    </small>
                                </div>
                            )}
                            {notificationPermission === 'default' && (
                                <div className="dropdown-item" style={{ backgroundColor: '#fff3cd', borderLeft: '3px solid #ffc107' }}>
                                    <small className="d-flex align-items-center justify-content-between flex-wrap">
                                        <span className="pr-2">
                                            <i className="fas fa-exclamation-triangle mr-1" style={{ color: '#856404' }} />
                                            Desktop notifications disabled
                                        </span>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-warning"
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (!('Notification' in window)) return;
                                                try {
                                                    const permission = await Notification.requestPermission();
                                                    setNotificationPermission(permission);
                                                    if (permission === 'granted') {
                                                        try {
                                                            new Notification('Notifications enabled', {
                                                                body: 'You will get alerts for new messages when this tab is in the background.',
                                                                icon: '/Assets/Images/color icon_circle.png'
                                                            });
                                                        } catch (_) {
                                                            /* ignore */
                                                        }
                                                        toast.success('Desktop notifications enabled!');
                                                    } else if (permission === 'denied') {
                                                        setNotificationPermission('denied');
                                                        toast.info(
                                                            'Notifications blocked. Click "How to Enable" to fix this.'
                                                        );
                                                    }
                                                } catch (err) {
                                                    // eslint-disable-next-line no-console
                                                    console.error(err);
                                                    toast.error('Could not request notification permission.');
                                                }
                                            }}
                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                        >
                                            <i className="fas fa-bell mr-1" />
                                            Enable
                                        </button>
                                    </small>
                                </div>
                            )}

                            <div className="dropdown-divider" />

                            {notifications.length === 0 ? (
                                <div className="dropdown-item text-center text-muted py-3">
                                    No notifications
                                </div>
                            ) : (
                                <>
                                    {(showAllNotifications ? notifications : notifications.slice(0, MAX_VISIBLE)).map((notification) => (
                                        <div key={notification._id}>
                                            <button
                                                type="button"
                                                className={`dropdown-item ${!notification.isRead ? "bg-light" : ""}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleNotificationClick(notification);
                                                }}
                                                style={{
                                                    whiteSpace: "normal",
                                                    cursor: "pointer",
                                                    width: "100%",
                                                    textAlign: "left",
                                                    border: "none",
                                                    background: "transparent"
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div style={{ flex: 1 }}>
                                                        <span style={{ fontSize: "14px", color: "#3498db", fontWeight: "500" }}>
                                                            {notification.title}
                                                        </span>
                                                        <p className="mb-1" style={{ fontSize: "12px", color: "#666" }}>
                                                            {notification.message}
                                                        </p>
                                                        <small className="text-muted">
                                                            <i className="far fa-clock mr-1" />
                                                            {formatNotificationTime(notification.createdAt)}
                                                        </small>
                                                    </div>
                                                    {!notification.isRead && (
                                                        <span className="badge badge-primary ml-2" style={{ fontSize: "8px" }}>
                                                            NEW
                                                        </span>
                                                    )}
                                                </div>
                                            </button>
                                            <div className="dropdown-divider" />
                                        </div>
                                    ))}

                                    {!showAllNotifications && notifications.length > MAX_VISIBLE && (
                                        <div className="dropdown-item text-center text-muted py-1">
                                            +{notifications.length - MAX_VISIBLE} more notifications
                                        </div>
                                    )}

                                    {notifications.length > MAX_VISIBLE && (
                                        <button
                                            type="button"
                                            className="dropdown-item dropdown-footer text-center"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setShowAllNotifications(!showAllNotifications);
                                            }}
                                            style={{ border: "none", width: "100%" }}
                                        >
                                            {showAllNotifications ? "Show Less" : "View All Notifications"}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </li>

                    {/* User Profile Dropdown */}
                    <li className="nav-item dropdown">
                        <button
                            className="nav-link p-1"
                            data-toggle="dropdown"
                            type="button"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                        >
                            <div className="user-panel d-flex">
                                <div className="image user_div">
                                    {profileImage && !avatarFailed ? (
                                        <img
                                            src={profileImage}
                                            alt={displayName}
                                            className="usercharacter"
                                            style={{
                                                objectFit: "cover",
                                                padding: 0,
                                                verticalAlign: "middle"
                                            }}
                                            onError={() => setAvatarFailed(true)}
                                        />
                                    ) : (
                                        <span className="usercharacter">{initial}</span>
                                    )}
                                </div>
                                <div className="info">
                                    <span className="d-block f_black">{displayName} <i className="fa-solid fa-angle-down" /></span>
                                </div>
                            </div>
                        </button>
                        <div className="dropdown-menu dropdown-menu-right">
                            <Link to="/profile" className="dropdown-item mb-2">
                                <i className="fa-solid fa-user mr-2" /> Profile
                            </Link>
                            <button
                                onClick={handleLogout}
                                className="dropdown-item"
                                type="button"
                                style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', padding: '0.25rem 1.1rem' }}
                            >
                                <i className="fa-solid fa-arrow-right-from-bracket mr-1" /> Logout
                            </button>
                        </div>
                    </li>
                </ul>
            </nav>
        </div>
    );
};

export default Header;